import { contrastRatio, luminance, markColor as sharedMarkColor } from "@teamna/shared";

export { contrastRatio, luminance };

/**
 * The twelve group colours, by index, the same list as `GROUP_PALETTE` in the API and
 * `groupPalette` in the mobile theme, so a business's third group is the same hue on every device.
 */
export const GROUP_PALETTE = [
  "#4895ED",
  "#2BB673",
  "#F2A93B",
  "#E15B64",
  "#8E63D6",
  "#1FA8A8",
  "#D6669A",
  "#6C8B3C",
  "#C97A2C",
  "#3B6FD6",
  "#9A6B4A",
  "#5B5F73",
] as const;

/**
 * The colours an owner picks for a level (2026-10-21): the four medal colours first, because
 * swimming levels are often named after them, then common colours. Any of them is safe: the calendar
 * tints a block with it under dark text, and the member home makes it darker for white text
 * (`markColor`).
 */
export const LEVEL_COLOR_CHOICES = [
  { value: "#CD7F32", label: "Bronze" },
  { value: "#A6A9AE", label: "Silver" },
  { value: "#D4AF37", label: "Gold" },
  { value: "#E5E4E2", label: "Platinum" },
  { value: "#E15B64", label: "Red" },
  { value: "#F08A24", label: "Orange" },
  { value: "#F2C230", label: "Yellow" },
  { value: "#2BB673", label: "Green" },
  { value: "#1FA8A8", label: "Teal" },
  { value: "#4895ED", label: "Blue" },
  { value: "#2C4A8C", label: "Navy" },
  { value: "#8E63D6", label: "Purple" },
  { value: "#D6669A", label: "Pink" },
  { value: "#8B5E3C", label: "Brown" },
  { value: "#6B7280", label: "Grey" },
  { value: "#1F2430", label: "Black" },
] as const;

/** A group's own colour when it has one, else the palette colour at its index. */
export function groupColor(hex: string | null | undefined, index: number): string {
  if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const size = GROUP_PALETTE.length;
  return GROUP_PALETTE[((index % size) + size) % size]!;
}

/**
 * A session's colour on the owner calendar: its level's colour when the level is in `levels` (the
 * timetable editor's list), else the colour the session carries, else the palette colour at `index`.
 * The Week and Day views both use it, so a level has one colour on both.
 */
export function sessionColor(
  levels: readonly { id: string; color: string | null; colorIndex: number }[],
  groupId: string,
  fallback: string | null,
  index: number,
): string {
  const level = levels.find((row) => row.id === groupId);
  return level ? groupColor(level.color, level.colorIndex) : groupColor(fallback, index);
}


/**
 * A participant's mark colour on the member home: the group colour (or the first palette colour when
 * it is not `#rrggbb`), made darker until white text on it passes WCAG AA. The rule lives in
 * `@teamna/shared` (`mark-color.ts`), so the home model can keep two people's marks apart.
 */
export function markColor(hex: string): string {
  return sharedMarkColor(groupColor(hex, 0));
}
